import {
    FGCustomPanel,
    FGListen,
    FGMaskInput,
    FGNumberInput,
    FGSelectInput,
    FGTextAreaInput,
    FGTextInput,
    FieldGroup,
} from "nsitools-react";
import React from "react";
import {useHistory} from "react-router";
import * as Yup from "yup";

import {BreedApi, BreedDto, BreedTranslationDto} from "../../../../api";
import {ERoutes} from "../../../../ERoutes";
import {
    FGCountrySelect,
    FGMultiTranslate,
    FGSRSHCheckboxInput,
    FGSRSHDateMaskInput,
    SharepointButton,
    SmallFormGenerator,
} from "../../../../components";
import {useGlobalData} from "../../../../contexts";
import {useApiService, useCrudApi, useTl, useWindow} from "../../../../hooks";
import {useReferential} from "../../../../hooks/useReferential";
import {ETLCodes} from "../../../../locales";
import {FGNumberMaskInput} from "../../../../components/formGenerator/FGNumberMaskInput";
import {BreedHealthPackSelector} from "./BreedHealthPackSelector";

export interface IBreedDetailPageProps {
    editMode: boolean;
    breedId: number;
}

export const BreedDetailPage: React.FunctionComponent<IBreedDetailPageProps> = ({editMode, breedId}) => {
    const {t, tUnsafe} = useTl();
    const api = useApiService(BreedApi);
    const history = useHistory();
    const {languages} = useGlobalData();
    const [breedGroups, breedGroupsLoading] = useReferential((c) => c.getBreedGroups(), true, []);
    const [agility, agilityLoading] = useReferential((a) => a.getAgility(), false);
    const [veterinaries, veterinariesLoading] = useReferential((c) => c.getBreedAuthorizedVets(), true, []);
    const [grooming, groomingLoading] = useReferential((a) => a.getGrooming(), false);
    const [translatedAgility, setTranslatedAgilities] = React.useState([]);
    const [translatedGrooming, setTranslatedGroomings] = React.useState([]);

    React.useEffect(() => {
        if (!agilityLoading && agility) {
            const newAgility = agility.map((c) => {
                return {label: tUnsafe(c.label), value: c.value};
            });
            setTranslatedAgilities(newAgility);
        }
    }, [agility, agilityLoading, tUnsafe]);

    React.useEffect(() => {
        if (!groomingLoading && grooming) {
            const newGrooming = grooming.map((c) => {
                return {label: tUnsafe(c.label), value: c.value};
            });
            setTranslatedGroomings(newGrooming);
        }
    }, [grooming, groomingLoading, tUnsafe]);

    const {data, loading, deleteItem, deleting, saveItem, saving, refresh} = useCrudApi(
        {
            getApiFn: (id = breedId) =>
                id > 0
                    ? api.get(id)
                    : new BreedDto({
                        healthPackIds: [],
                        breedTranslations: [
                            ...languages.map(
                                (l) =>
                                    new BreedTranslationDto({
                                        languageId: l.languageId,
                                    })
                            ),
                        ],
                    }).toJSON(),
            saveApiFn: (d: BreedDto) => api.save(d),
            onSavedRoute: (d) => `${ERoutes.breeds}/${d.breedId}/detail/edit`,
            deleteApiFn: (d) => api.delete(d.breedId),
            onDeletedRoute: () => ERoutes.breeds,
        },
        [breedId]
    );

    const FormSchema = React.useMemo(() => {
        return Yup.object().shape({
            countryId: Yup.number().nullable().required(t(ETLCodes.Required)),
            breedGroupId: Yup.number().nullable().required(t(ETLCodes.Required)),
            officialName: Yup.string()
                .nullable()
                .required(t(ETLCodes.Required))
                .test("name_unique", t(ETLCodes.OfficialNameAlreadyExists), async function (value) {
                    const res = await api.officialNameUnique(breedId, value);
                    return res.value;
                }),
            breedTranslations: Yup.array().of(
                Yup.object().shape({
                    text: Yup.string().nullable().required(t(ETLCodes.Required)),
                })
            ),
            knownAt: Yup.date()
                .nullable()
                .test("required", t(ETLCodes.Required), async function (value) {
                    if (this.parent.knownBreed) {
                        return value !== null && value !== undefined;
                    }
                    return true;
                }),
        });
    }, [api, breedId, t]);

    const [shpLoading, setShpLoading] = React.useState(false);
    const existsInShp = React.useMemo(() => !!data?.sharepointUri, [data?.sharepointUri]);
    const openOrCreateInShp = React.useCallback(async () => {
        setShpLoading(true);
        if (!!data?.sharepointUri) {
            window.open(data?.sharepointUri, "_blank");
            setShpLoading(false);
        } else {
            try {
                await api.createInSharepoint(breedId);
                refresh();
            } catch (e) {
                throw e;
            } finally {
                setShpLoading(false);
            }
        }
    }, [api, breedId, data?.sharepointUri, refresh]);

    const {openNewTab} = useWindow();
    return (
        <SmallFormGenerator
            initialValues={data}
            onSubmit={saveItem}
            editMode={editMode}
            validationSchema={FormSchema}
            loading={loading}
            onCancel={() => history.push(ERoutes.breeds)}
            onDelete={deleteItem}
            showDeleteButton={breedId > 0}
            saving={saving}
            deleting={deleting}
        >
            <FGListen
                field="knownBreed"
                onChanged={(value, formik) => {
                    if (!value && !!formik.values["knownAt"]) {
                        formik.setFieldValue("knownAt", null);
                    }
                    if (formik.values.internship && value && !!formik.values["internshipDate"] && !!formik.values["internship"]) {
                       formik.setFieldValue("internshipDate", null);
                       formik.setFieldValue("internship", false);
                     }
                }}
            />
            <FGListen
                field="internship"
                onChanged={(value, formik) => {
                    if (!value && !!formik.values["internshipDate"]) {
                        formik.setFieldValue("internshipDate", null);
                    }
                    if (formik.values.knownBreed && value && !!formik.values["knownAt"] && !!formik.values["knownBreed"]) {
                        formik.setFieldValue("knownAt", null);
                        formik.setFieldValue("knownBreed", false);
                    }
                }}
            />
            <FieldGroup
                fieldsetProps={{
                    title: t(ETLCodes.Breed),
                    rightElement: breedId > 0 && (
                        <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp}/>
                    ),
                }}
            >
                <FGMultiTranslate
                    label={ETLCodes.Name}
                    translationField="text"
                    translationsArrayField="breedTranslations"
                    translationDto={BreedTranslationDto}
                />
                <FGTextInput label={t(ETLCodes.OfficialName)} name="officialName"/>
                <FGCountrySelect
                    name="countryId"
                    label={t(ETLCodes.Country)}
                    requiredMark
                    onItemViewClick={(item) => openNewTab(`${ERoutes.country}/${item}/detail/view`)}
                />
                <FGSelectInput
                    name="breedGroupId"
                    label={t(ETLCodes.BreedGroupLabel)}
                    items={breedGroups}
                    loading={breedGroupsLoading}
                    requiredMark
                    onItemViewClick={(item: number) => openNewTab(`${ERoutes.breedGroups}/${item}/detail/view`)}
                />
                <FGNumberInput name="breedNumber" label={t(ETLCodes.BreedNumber)}/>
                <FGNumberMaskInput name="varietyNumber" label={t(ETLCodes.VarietyNumber)}/>
                <FGNumberMaskInput name="fciNumber" label={t(ETLCodes.FciNumber)}/>
                <FGSRSHCheckboxInput name="knownBreed" label={t(ETLCodes.KnownBreed)}/>
                <FGCustomPanel>
                    {(ctx) =>
                        ctx.formik.values.knownBreed &&
                        <FGSRSHDateMaskInput name="knownAt" label={t(ETLCodes.BreedKownDate)}/>
                    }
                </FGCustomPanel>
                <FGSRSHCheckboxInput name="internship" label={t(ETLCodes.Internship)}/>
                <FGCustomPanel>
                    {(ctx) =>
                        ctx.formik.values.internship && (
                            <FGSRSHDateMaskInput name="internshipDate" label={t(ETLCodes.InternshipDate)}/>
                        )
                    }
                </FGCustomPanel>

                <FGSRSHCheckboxInput name="subjectToWork" label={t(ETLCodes.SubjectToWork)}/>
                <FGSRSHCheckboxInput name="breeding" label={t(ETLCodes.BreedingAuthorized)}/>
                <FGSelectInput name="agility" label={t(ETLCodes.Agility)} items={translatedAgility}
                               loading={agilityLoading}/>

                <FGSelectInput
                    name="grooming"
                    label={t(ETLCodes.Grooming)}
                    items={translatedGrooming}
                    loading={groomingLoading}
                />
                <FGSelectInput
                    name="userId"
                    label={t(ETLCodes.Veterinary)}
                    items={veterinaries}
                    loading={veterinariesLoading}
                />
                <FGSRSHCheckboxInput name="standardConfirmation" label={t(ETLCodes.StandardConfirmation)}/>

                <FGSRSHCheckboxInput name="shortenedTail" label={t(ETLCodes.ShortenedTail)}/>

                <FGSRSHCheckboxInput name="merleColorAuthorized" label={t(ETLCodes.MerleColorAuthorized)}/>

                <FGNumberInput name="minDysplasiaAgeInMonths" label={t(ETLCodes.MinDysplasiaAgeInMonths)}/>
                <FGNumberInput name="number" label={t(ETLCodes.Number)}/>

                <FGMaskInput
                    cleaveOptions={{
                        numericOnly: true,
                        numeral: true,
                        numeralPositiveOnly: true,
                    }}
                    name="sizeMin"
                    label={t(ETLCodes.SizeFrom) + ` (cm)`}
                />
                <FGMaskInput
                    cleaveOptions={{
                        numericOnly: true,
                        numeral: true,
                        numeralPositiveOnly: true,
                    }}
                    name="sizeMax"
                    label={t(ETLCodes.SizeTo) + ` (cm)`}
                />
                <FGMaskInput
                    cleaveOptions={{
                        numericOnly: true,
                        numeral: true,
                        numeralPositiveOnly: true,
                    }}
                    name="weightMin"
                    label={t(ETLCodes.WeightFrom) + ` (kg)`}
                />
                <FGMaskInput
                    cleaveOptions={{
                        numericOnly: true,
                        numeral: true,
                        numeralPositiveOnly: true,
                    }}
                    name="weightMax"
                    label={t(ETLCodes.WeightTo) + ` (kg)`}
                />
                <FGTextAreaInput name="remark" label={t(ETLCodes.Remark)}/>
            </FieldGroup>
            <FGCustomPanel>
                {({formik}) => (
                    <BreedHealthPackSelector
                        linkedIds={formik?.values?.healthPackIds || null}
                        onLinkedIdsChanged={(ids) => formik?.setFieldValue("healthPackIds", ids)}
                        editMode={editMode}
                        loading={loading}
                    />
                )}
            </FGCustomPanel>
        </SmallFormGenerator>
    );
};
